import React, { useCallback } from "react";
import Modal from "components/Modal/Modal";
import Typography from "components/Typography";
import { arrowBackIcon } from "components/Icon";
import { ReactComponent as RentalProtectionLogo } from "./assets/rental-protection-logo.svg";
import classes from "./GoodsamInsuranceModal.module.css";
import cn from 'classnames';
import { GoodsamInsuranceDesktopTable, GoodsamInsuranceModalFooter, GoodsamInsuranceMobileTable } from "./components";
import { buildStructuredDesktopTable, buildTableDesktopThs, buildStructuredMobileTable, buildTableMobileThs } from "./utils";
import { COVERAGE_INFO_BUTTON } from "./constants";
import { useInsuranceData, useSelectedPlan } from "./hooks";
import { SpinnerBack } from "components/Spinner/Spinner";
import Button from "components/Button";

export const GoodsamInsuranceModal = (props) => {
  const {
    show,
    onClose,
    data = null,
    backTo,
    rentalType,
    rvId,
    selectedPlanId = null,
    onSelectPlan = () => {}
  } = props;

  const { currentData, loading } = useInsuranceData(show, data, rvId, rentalType);
  const { selectedPlan, saveSelectedPlan, savedPlanId } = useSelectedPlan(currentData.plans, selectedPlanId);

  const onSaveSelectedPlan = useCallback((plan) => {
    if (plan?.id) {
      saveSelectedPlan(plan.id);
    }
  }, [saveSelectedPlan]);

  const handleSelectPlan = useCallback(() => {
    onSelectPlan({
      rvId,
      id: savedPlanId,
      rentalType,
      name: selectedPlan.name,
      planData: selectedPlan,
      insurancePackagesDetailedResult: currentData
    });
  }, [currentData, onSelectPlan, rentalType, rvId, savedPlanId, selectedPlan]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      modalWrapClassnames={classes.modalWrap}
      modalClassnames={classes.modal}
      modalCrossButtonClassname={classes.crossButton}
      showCross={!backTo}
    >
      <div className={classes.modalInner}>
        <header className={cn(classes.header, backTo && classes.headerBackTo)}>
          {backTo && (
            <div className={classes.headerBack}>
              <Button
                ghost
                onClick={onClose}
                className={classes.btnBack}
              >
                <span className={classes.btnBackInner}>
                  <span className={classes.btnBackIcon}>
                    {arrowBackIcon}
                  </span>
                  <Typography className={cn('underline', classes.btnBackText)} variant="body" size="l" component="span">
                    {backTo}
                  </Typography>
                </span>
              </Button>
            </div>
          )}
        </header>
        <div className={classes.content}>
          <div className={classes.logo}>
            <RentalProtectionLogo />
          </div>

          <Typography variant="subtitle" size="s" className={classes.subtitle}>
            Choose the plan that’s right for you.
          </Typography>

          <div className={cn(classes.table, "dn db-l")}>
            {loading ? (
              <SpinnerBack />
            ) : (
              <GoodsamInsuranceDesktopTable
                tableTds={buildStructuredDesktopTable(currentData)}
                tableThs={buildTableDesktopThs(currentData.plans || [])}
                selectedPlan={selectedPlan}
                onSelectInsurance={onSaveSelectedPlan}
                plans={currentData.plans || []}
              />
            )}
          </div>

          <div className={cn(classes.tableMobile, "dn-l")}>
            {loading ? (
              <SpinnerBack />
            ) : (
              <GoodsamInsuranceMobileTable
                tableTds={buildStructuredMobileTable(buildStructuredDesktopTable(currentData), currentData.plans)}
                tableThs={buildTableMobileThs(currentData.plans || [])}
                selectedPlan={selectedPlan}
                onSelectInsurance={onSaveSelectedPlan}
                plans={currentData.plans || []}
              />
            )}
          </div>

          <div className="tac fw mt-l-8">
            <Button {...COVERAGE_INFO_BUTTON} />
          </div>
        </div>

        <footer className={classes.footer}>
          <div className={classes.footerInner}>
            <GoodsamInsuranceModalFooter isDisabled={loading} onClose={onClose} onSelectPlan={handleSelectPlan}/>
          </div>
        </footer>
      </div>
    </Modal>
  );
};
